import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Button, IconButton } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { PZAPlot } from './plot';
import { PZATable } from './table';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { MaterialDropdown } from 'console/shared/components/fields';
import { preventDefault, useBox } from 'shared/hooks/utils';
import { createPredefinedCurve, equalsToPredefinedCurve, predefinedCurveNumbers, } from 'console/device-settings/z3k/components/utils/pza-pane/utils';
import { dk2g, g2dk } from 'utils';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { useBooleanState } from 'shared/hooks/state/boolean';
function PZAPaneImpl(props) {
    const { onAdd, onRemove, onChange, value: pza, onChangeSelection, validation_result, validation_warnings_result, has_outside_temperature_source, notChosenTitle, pzas, } = props;
    const pzaBox = useBox(pza);
    const [open_dialog, closeDialog, openDialog] = useBooleanState(false);
    const [pza_name, setPzaName] = useState('');
    const addNewPZA = useCallback(() => {
        closeDialog();
        const new_pza = {
            name: pza_name,
            x: [g2dk(20), g2dk(-40)],
            y: [g2dk(20), g2dk(100)],
        };
        onAdd(new_pza);
    }, [closeDialog, pza_name, onAdd]);
    const removePZA = useCallback(() => {
        if (pzaBox.current == null) {
            return;
        }
        onRemove(pzaBox.current);
    }, [onRemove]);
    const onChangePZAPlot = useCallback((temps) => {
        if (pzaBox.current == null) {
            return;
        }
        const { x, y } = Object.fromEntries(['x', 'y'].map((key, index) => [
            key,
            temps.map((temp) => g2dk(temp[index])),
        ]));
        onChange({
            ...pzaBox.current,
            x,
            y,
        });
    }, [onChange]);
    const onSetPredefinedCurve = useCallback((new_id) => {
        onChangePZAPlot(createPredefinedCurve(new_id));
    }, [onChangePZAPlot, createPredefinedCurve]);
    const onChangePza = useCallback((new_id) => {
        if (new_id === -1) {
            setPzaName('Кривая ПЗА');
            openDialog();
            return;
        }
        if (new_id === 'null') {
            return onChangeSelection(null);
        }
        onChangeSelection(new_id);
    }, [onChangeSelection, setPzaName, openDialog]);
    const options = useMemo(() => [
        { text: notChosenTitle ?? 'ПЗА не используется', value: 'null' },
        ...(pzas ?? []).map(({ id, name }) => ({ text: name, value: id })),
        { text: 'Добавить новый ПЗА', value: -1 },
    ], [notChosenTitle, pzas]);
    const predefined_options = useMemo(() => [
        { text: 'Своя кривая', value: 0 },
        ...predefinedCurveNumbers.map((curveNo) => ({
            text: `Кривая ${curveNo.toFixed(1)}`,
            value: curveNo,
        })),
    ], [predefinedCurveNumbers]);
    const choose_pza = useMemo(() => (pza == null ? 'null' : pza.id), [pza]);
    const xy = useMemo(() => pza != null ? pza.x.map((_, i) => [dk2g(pza.x[i]), dk2g(pza.y[i])]) : [], [pza]);
    const onKeyDownPzaNameField = useCallback((event) => {
        if (event.key === 'Enter') {
            preventDefault(event);
            addNewPZA();
        }
    }, [preventDefault, addNewPZA]);
    const pza_name_field = useCallback((input_props) => (_jsx(TextInputWrapper, { ...input_props, autoFocus: true, placeholder: "\u041D\u043E\u0432\u044B\u0439 \u041F\u0417\u0410", onKeyDown: onKeyDownPzaNameField })), [onKeyDownPzaNameField]);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "flex", flexDirection: "column", gap: 2, width: "100%", children: [_jsxs(Box, { display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexGrow: 1, children: [_jsx(ControlWithoutValidation, { label: "\u041A\u0440\u0438\u0432\u0430\u044F \u041F\u0417\u0410", value: choose_pza, onChange: onChangePza, field: (input_props) => (_jsx(MaterialDropdown, { ...input_props, options: options })) }), pza && (_jsx(IconButton, { onClick: removePZA, size: "large", children: _jsx(Delete, {}) }))] }), pza && (_jsxs(_Fragment, { children: [_jsx(ControlWithoutValidation, { label: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u043E\u0434\u043D\u0443 \u0438\u0437 \u0441\u0442\u0430\u043D\u0434\u0430\u0440\u0442\u043D\u044B\u0445 \u043A\u0440\u0438\u0432\u044B\u0445", value: predefinedCurveNumbers.find((k) => equalsToPredefinedCurve(xy, k)) ?? 0, onChange: onSetPredefinedCurve, field: (input_props) => (_jsx(MaterialDropdown, { ...input_props, options: predefined_options })) }), _jsx(Box, { flexGrow: 1, children: _jsx(PZAPlot, { temps: xy, onChange: onChangePZAPlot }) }), _jsx(PZATable, { temps: xy, onChange: onChangePZAPlot })] })), validation_result && (_jsx(Alert, { severity: "error", children: validation_result })), !validation_result && validation_warnings_result && (_jsx(Alert, { severity: "warning", children: validation_warnings_result })), pza && !has_outside_temperature_source && (_jsxs(Alert, { severity: "warning", children: [_jsx(AlertTitle, { children: "\u0423\u043B\u0438\u0447\u043D\u044B\u0439 \u0434\u0430\u0442\u0447\u0438\u043A \u043D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D." }), "\u0414\u043B\u044F \u043A\u043E\u0440\u0440\u0435\u043A\u0442\u043D\u043E\u0439 \u0440\u0430\u0431\u043E\u0442\u044B \u041F\u0417\u0410 \u0432\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0434\u0430\u0442\u0447\u0438\u043A \u0438\u043B\u0438 \u0443\u043A\u0430\u0436\u0438\u0442\u0435 \u043C\u0435\u0441\u0442\u043E\u043F\u043E\u043B\u043E\u0436\u0435\u043D\u0438\u0435 \u043F\u0440\u0438\u0431\u043E\u0440\u0430 \u043D\u0430 \u0432\u043A\u043B\u0430\u0434\u043A\u0435 \"\u041E\u0431\u0449\u0438\u0435 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438\" \u0434\u043B\u044F \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u0438\u044F \u043F\u043E\u0433\u043E\u0434\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 \u0438\u0437 \u0438\u043D\u0442\u0435\u0440\u043D\u0435\u0442\u0430."] }))] }), _jsx(MessageBox, { open: open_dialog, onClose: closeDialog, footer: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: closeDialog, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { onClick: addNewPZA, color: "primary", children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C" })] }), children: _jsx(ControlWithoutValidation, { label: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0438\u043C\u044F \u043D\u043E\u0432\u043E\u0433\u043E \u041F\u0417\u0410", value: pza_name, onChange: setPzaName, field: pza_name_field }) })] }));
}
export const PZAPane = memo(PZAPaneImpl);
